import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Typography,
  Paper,
  Divider,
  Box,
} from "@material-ui/core";
import SecurityIcon from "@material-ui/icons/Security";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    overflowY: "auto",
    backgroundColor: "#f5f7fa",
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(8),
  },
  paper: {
    padding: theme.spacing(5),
    borderRadius: "16px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  icon: {
    fontSize: 40,
    color: theme.palette.primary.main,
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
  },
  subtitle: {
    color: "#6b7280",
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontWeight: 600,
    color: "#1a1a2e",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
  },
  body: {
    color: "#374151",
    lineHeight: 1.8,
    marginBottom: theme.spacing(1.5),
  },
  divider: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  footer: {
    marginTop: theme.spacing(5),
    padding: theme.spacing(2.5),
    backgroundColor: "#f0f4ff",
    borderRadius: "12px",
    borderLeft: "4px solid " + theme.palette.primary.main,
  },
  footerText: {
    color: "#374151",
    fontSize: "0.9rem",
  },
  backLink: {
    display: "inline-block",
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontWeight: 500,
    fontSize: "0.95rem",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const PrivacyPolicy = () => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Container maxWidth="md">
        <a href="/login" className={classes.backLink}>
          ← Voltar para o login
        </a>

        <Paper className={classes.paper}>
          <div className={classes.header}>
            <SecurityIcon className={classes.icon} />
            <Typography variant="h4" component="h1" className={classes.title}>
              Política de Privacidade
            </Typography>
          </div>

          <Typography variant="body2" className={classes.subtitle}>
            ZapConverse — Última atualização: 27 de abril de 2026
          </Typography>

          <Divider />

          <Typography variant="body1" className={classes.body} style={{ marginTop: 24 }}>
            A <strong>ZapConverse</strong> é uma plataforma SaaS de automação de atendimento via WhatsApp,
            comprometida com a proteção da privacidade e dos dados pessoais de seus usuários.
            Esta Política de Privacidade descreve como coletamos, utilizamos, armazenamos e protegemos as
            informações fornecidas ao utilizar nossa plataforma de chatbot e atendimento automatizado via
            WhatsApp Business API.
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Ao utilizar nossa plataforma, você concorda com os termos desta Política de Privacidade, em
            conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018)</strong>.
          </Typography>

          {/* Seção 1 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            1. Dados Coletados
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Para a prestação dos nossos serviços, podemos coletar os seguintes dados pessoais:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li><strong>Dados de identificação:</strong> nome completo, endereço de e-mail e número de telefone (WhatsApp);</li>
              <li><strong>Dados de autenticação:</strong> credenciais de acesso à plataforma (e-mail e senha criptografada);</li>
              <li><strong>Dados de comunicação:</strong> conteúdo das mensagens trocadas via WhatsApp no âmbito do atendimento automatizado;</li>
              <li><strong>Dados de uso:</strong> informações sobre como você utiliza a plataforma, incluindo logs de acesso, endereço IP e dados de sessão;</li>
              <li><strong>Dados da empresa:</strong> nome empresarial e informações de contato fornecidas no cadastro.</li>
            </ul>
          </Typography>

          {/* Seção 2 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            2. Finalidade do Uso dos Dados
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Os dados coletados são utilizados exclusivamente para:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>Prestação e operação da plataforma de chatbot e atendimento automatizado via WhatsApp;</li>
              <li>Autenticação e controle de acesso de usuários à plataforma;</li>
              <li>Envio e recebimento de mensagens por meio da WhatsApp Business API (Cloud API da Meta);</li>
              <li>Análise de desempenho e melhoria contínua dos serviços;</li>
              <li>Comunicações relacionadas ao suporte técnico e atualizações do sistema;</li>
              <li>Cumprimento de obrigações legais e regulatórias.</li>
            </ul>
          </Typography>

          {/* Seção 3 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            3. Integração com WhatsApp Business API (Cloud API da Meta)
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Nossa plataforma opera por meio da <strong>WhatsApp Business API (Cloud API)</strong>, fornecida pela{" "}
            <strong>Meta Platforms, Inc.</strong> Para viabilizar o envio e recebimento de mensagens, dados como
            número de telefone, nome de exibição e conteúdo das mensagens são processados pela infraestrutura
            da Meta, conforme os{" "}
            <a href="https://www.whatsapp.com/legal/business-policy" target="_blank" rel="noopener noreferrer">
              Termos de Serviço do WhatsApp Business
            </a>{" "}
            e a{" "}
            <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">
              Política de Privacidade da Meta
            </a>.
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Ao utilizar nossa plataforma, você está ciente e consente com o processamento de dados pela Meta
            nos termos de suas próprias políticas. Recomendamos a leitura das políticas da Meta para maior
            compreensão.
          </Typography>

          {/* Seção 4 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            4. Armazenamento e Segurança dos Dados
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Os dados são armazenados em servidores seguros e protegidos por medidas técnicas e
            organizacionais adequadas, incluindo:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>Criptografia de senhas e dados sensíveis;</li>
              <li>Controle de acesso restrito aos dados;</li>
              <li>Uso de protocolo HTTPS para todas as comunicações;</li>
              <li>Monitoramento e auditoria de acessos.</li>
            </ul>
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Os dados pessoais são retidos pelo tempo necessário para a prestação dos serviços ou conforme
            exigido pela legislação vigente. Após o encerramento da conta, os dados poderão ser anonimizados
            ou excluídos, respeitado o prazo legal aplicável.
          </Typography>

          {/* Seção 5 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            5. Compartilhamento com Terceiros
          </Typography>
          <Typography variant="body1" className={classes.body}>
            A ZapConverse não vende, aluga ou comercializa seus dados pessoais. O compartilhamento
            ocorre apenas nas seguintes situações:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li><strong>Meta Platforms, Inc.:</strong> para processamento das mensagens via WhatsApp Business API;</li>
              <li><strong>Prestadores de serviços:</strong> parceiros técnicos que auxiliam na operação da plataforma, mediante acordos de confidencialidade;</li>
              <li><strong>Obrigação legal:</strong> quando exigido por lei, decisão judicial ou autoridade competente.</li>
            </ul>
          </Typography>

          {/* Seção 6 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            6. Direitos do Usuário (LGPD)
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Conforme a LGPD, você possui os seguintes direitos em relação aos seus dados pessoais:
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li><strong>Confirmação e acesso:</strong> confirmar a existência e acessar seus dados;</li>
              <li><strong>Correção:</strong> solicitar a correção de dados incompletos, inexatos ou desatualizados;</li>
              <li><strong>Anonimização ou eliminação:</strong> solicitar a anonimização, bloqueio ou exclusão de dados desnecessários;</li>
              <li><strong>Portabilidade:</strong> solicitar a portabilidade dos dados a outro fornecedor;</li>
              <li><strong>Revogação do consentimento:</strong> revogar o consentimento a qualquer momento;</li>
              <li><strong>Informação:</strong> ser informado sobre os terceiros com os quais compartilhamos seus dados.</li>
            </ul>
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Para exercer qualquer um desses direitos, entre em contato conosco pelo e-mail indicado na seção 8.
          </Typography>

          {/* Seção 7 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            7. Cookies e Tecnologias Similares
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Nossa plataforma pode utilizar cookies e tecnologias similares para melhorar a experiência de uso,
            manter sessões autenticadas e coletar estatísticas de uso. Você pode configurar seu navegador
            para recusar cookies, mas isso pode impactar o funcionamento de algumas funcionalidades.
          </Typography>

          {/* Seção 8 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            8. Exclusão de Dados do Usuário
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Em conformidade com a <strong>LGPD (Lei nº 13.709/2018)</strong> e o <strong>GDPR (General Data
            Protection Regulation)</strong>, você tem o direito de solicitar a <strong>exclusão completa de todos
            os seus dados pessoais</strong> armazenados pela ZapConverse a qualquer momento.
          </Typography>

          <Typography variant="body1" className={classes.body}>
            <strong>Como solicitar a exclusão:</strong>
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ol style={{ paddingLeft: 20, margin: 0 }}>
              <li>
                <strong>Pelo painel do ZapConverse:</strong> acesse <em>Configurações &gt; Conta &gt; Excluir
                minha conta</em> e siga as instruções apresentadas;
              </li>
              <li>
                <strong>Por e-mail:</strong> envie uma mensagem para{" "}
                <a href="mailto:contato@zapconverse.com">contato@zapconverse.com</a> com o assunto{" "}
                <em>"Solicitação de Exclusão de Dados"</em>, informando o e-mail cadastrado na conta.
              </li>
            </ol>
          </Typography>

          <Typography variant="body1" className={classes.body}>
            <strong>Prazo de atendimento:</strong> a solicitação será processada em até <strong>30 dias</strong>{" "}
            corridos após o recebimento e confirmação do pedido.
          </Typography>

          <Typography variant="body1" className={classes.body}>
            <strong>O que será excluído:</strong>
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>Dados pessoais de identificação (nome, e-mail, telefone);</li>
              <li>Histórico de conversas e mensagens;</li>
              <li>Contatos e listas cadastradas;</li>
              <li>Configurações e preferências da conta;</li>
              <li>Histórico de uso e logs associados à conta.</li>
            </ul>
          </Typography>

          <Typography variant="body1" className={classes.body}>
            <strong>O que pode ser mantido após a exclusão:</strong>
          </Typography>
          <Typography variant="body1" className={classes.body} component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>
                <strong>Dados anonimizados</strong> para fins estatísticos e de melhoria do serviço, que não
                permitem a identificação do titular;
              </li>
              <li>
                <strong>Registros legais obrigatórios</strong> (como notas fiscais, comprovantes de pagamento
                e registros fiscais), pelo prazo determinado pela legislação vigente.
              </li>
            </ul>
          </Typography>

          <Typography variant="body1" className={classes.body}>
            <strong>Atenção:</strong> após a conclusão do processo de exclusão,{" "}
            <strong>a conta não poderá ser recuperada</strong>. Todos os dados, configurações e histórico
            serão permanentemente removidos de nossos sistemas ativos.
          </Typography>

          {/* Seção 9 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            9. Contato e Encarregado de Dados (DPO)
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Para dúvidas, solicitações ou reclamações relacionadas ao tratamento de dados pessoais, entre em
            contato com o responsável pela proteção de dados:
          </Typography>
          <Typography variant="body1" className={classes.body}>
            <strong>E-mail:</strong>{" "}
            <a href="mailto:contato@zapconverse.com">contato@zapconverse.com</a>
          </Typography>

          {/* Seção 10 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            10. Alterações nesta Política
          </Typography>
          <Typography variant="body1" className={classes.body}>
            Esta Política de Privacidade pode ser atualizada periodicamente. Notificaremos os usuários sobre
            mudanças relevantes por meio da plataforma ou por e-mail. O uso continuado da plataforma após
            as alterações implica na aceitação da nova versão.
          </Typography>

          <Divider className={classes.divider} />

          <Box className={classes.footer}>
            <Typography variant="body2" className={classes.footerText}>
              <strong>ZapConverse</strong> — Esta política entra em vigor em 27 de abril de 2026.
              Dúvidas? Entre em contato: <a href="mailto:contato@zapconverse.com">contato@zapconverse.com</a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;
